import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, StatusBadge, formatCurrency, formatDate } from "@/components/shared";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function InvoicesPage() {
  const { currentPark } = usePark();
  const [invoices, setInvoices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({
    tenant_id: "", description: "Monthly lot rent",
    item_desc: "Lot rent", item_amount: 750, tax_rate: 0.11,
  });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [i, t] = await Promise.all([
        api.get(`/invoices${filter ? `?status=${filter}` : ""}`),
        api.get("/tenants"),
      ]);
      setInvoices(i.data); setTenants(t.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (currentPark?.id) load(); }, [filter, currentPark?.id]);

  const tenantLabel = (id) => {
    const t = tenants.find((x) => x.id === id);
    return t ? `${t.first_name} ${t.last_name}` : "—";
  };

  const create = async () => {
    if (!form.tenant_id) { toast.error("Tenant is required"); return; }
    try {
      await api.post("/invoices", {
        tenant_id: form.tenant_id,
        description: form.description,
        items: [{ description: form.item_desc, amount: parseFloat(form.item_amount) || 0 }],
        tax_rate: parseFloat(form.tax_rate) || 0,
      });
      toast.success("Invoice created");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Billing & Invoices"
        subtitle={`${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" data-testid="new-invoice-button">
                <Plus size={14} className="mr-2" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="!bg-white border-[#EAEAE5]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Outfit" }}>New Invoice</DialogTitle>
                <DialogDescription className="text-xs text-[#737A76]">Create an invoice for a tenant with a custom line item.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#737A76]">Tenant</Label>
                  <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                    <SelectTrigger data-testid="inv-tenant-select"><SelectValue placeholder="Select tenant…" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Item</Label><Input value={form.item_desc} onChange={(e) => setForm({ ...form, item_desc: e.target.value })} /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Amount</Label><Input type="number" value={form.item_amount} onChange={(e) => setForm({ ...form, item_amount: e.target.value })} data-testid="inv-amount-input" /></div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-[#737A76]">Tax rate (e.g. 0.11)</Label><Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#284B3B] hover:bg-[#1E3F32] text-white" onClick={create} data-testid="create-invoice-submit">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-white border-[#EAEAE5]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Open">Open</SelectItem>
            <SelectItem value="Partial">Partial</SelectItem>
            <SelectItem value="Paid">Paid</SelectItem>
            <SelectItem value="Void">Void</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-[#EAEAE5] shadow-none overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm pd-table">
            <thead><tr className="text-left border-b border-[#EAEAE5] bg-[#FAFAF7]">
              <th className="px-4 py-3">Invoice</th><th>Tenant</th><th>Date</th><th>Description</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={8} className="px-4 py-8 text-center text-[#737A76]">Loading…</td></tr>
              : invoices.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-[#737A76]">No invoices.</td></tr>
              : invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[#F0F0EC] last:border-0 cursor-pointer" onClick={() => navigate(`/invoices/${inv.id}`)} data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-medium">{inv.invoice_no}</td>
                  <td>{tenantLabel(inv.tenant_id)}</td>
                  <td>{formatDate(inv.date)}</td>
                  <td className="text-[#737A76]">{inv.description}</td>
                  <td>{formatCurrency(inv.total)}</td>
                  <td>{formatCurrency(inv.paid)}</td>
                  <td>{formatCurrency(inv.balance)}</td>
                  <td><StatusBadge status={inv.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
